import React, { ReactNode, useEffect, useState } from "react";
import Button from "../../../common/Button";
import { ButtonTypes, ButtonVariants } from "../../../common/Button/constants";
import { Formik } from "formik";
import * as Yup from "yup";
import FormikFormLine from "../../../formik-form-components/FormikFormLine";
import { BIN_ID_PREXIX } from "../../../../utils/parts";
import { OBJECT_ID_REGEX } from "../../../../utils/forms";
import { ButtonContainer } from "./InventoryBinFormStyles";
import FlashPCBSelect from "../../../common/FlashPCBSelect";
import { BoardOptionFromId } from "../../../selectOptions/BoardOption";
import { useAppSelector } from "../../../../utils/hooks/store";
import LoadingOverlay from "../../../LoadingOverlay";
import { useCustomAlert } from "../../../../utils/hooks/alerts";
import { SingleValue } from "react-select";
import useUpdateInventoryBin from "../../../../utils/hooks/inventory/updateInventoryBin";
import FormikSelect from "../../../formik-form-components/FormikSelect";
import { useFlashPCBSettings } from "../../../../utils/hooks/commonly-stored/flashPCBSettings";
import {
  BinType,
  FeederWidthMM,
  InventoryBin,
} from "../Bins/inventoryBinTypes";
import FormikCheckbox from "../../../formik-form-components/FormikCheckbox";
import useManufacturingDashboard from "../../../../utils/hooks/manfacturingDashboard";
import { Board } from "../../../../reducers/uploadedBoard";
import FormikNumberInput from "../../../formik-form-components/FormikNumberInput";

export const FLASHPCB_BIN_BARCODE_REGEX = new RegExp(
  `(^${BIN_ID_PREXIX}${OBJECT_ID_REGEX}$)`,
);

const feederDetailsSchema = Yup.object({
  width_mm: Yup.number().required("Feeder width is required for feeder bin"),
});

const trayDetailsSchema = Yup.object({
  num_columns: Yup.number().nullable(),
  num_rows: Yup.number().nullable(),
  slot_depth_mm: Yup.number().nullable(),
  slot_width_mm: Yup.number().nullable(),
  slot_height_mm: Yup.number().nullable(),
});

const validationSchema = Yup.object().shape({
  flashpcb_bin_barcode: Yup.string()
    .matches(FLASHPCB_BIN_BARCODE_REGEX)
    .required("Bin barcode is required"),
  bin_location: Yup.string().required("Bin location is required"),
  bin_name: Yup.string().required("Bin name is required"),
  bin_type: Yup.string()
    .required("Bin type is required")
    .when("bin_name", {
      is: (value: string) => {
        if (!value) {
          return false;
        }
        return (
          value.toLowerCase().includes("hanwha") ||
          value.toLowerCase().includes("feeder")
        );
      },
      then: () =>
        Yup.string().oneOf(
          [BinType.FEEDER],
          "Bin name indicates this should be a feeder bin",
        ),
    }),
  can_allocate_from_this_bin: Yup.boolean().required("This field is required"),
  feeder_details: Yup.object().when("bin_type", {
    is: BinType.FEEDER,
    then: () => feederDetailsSchema.required(),
    otherwise: () => Yup.object().nullable(),
  }),

  tray_details: Yup.object().when("bin_type", {
    is: BinType.TRAY,
    then: () => trayDetailsSchema.required(),
    otherwise: () => Yup.object().nullable(),
  }),
});

const FORM_INFO = [
  {
    name: "flashpcb_bin_barcode",
    label: "FlashPCB Bin Barcode",
    placeholder: "Scan FlashPCB bin barcode",
  },
  {
    name: "bin_location",
    label: "Bin Location",
    placeholder: "Enter bin location",
  },
  {
    name: "bin_name",
    label: "Bin name",
    placeholder: "Enter bin name",
  },
];

interface FeederDetailsFormValues {
  width_mm: FeederWidthMM | null;
}

interface TrayDetailsFormValues {
  num_columns: number | null;
  num_rows: number | null;
  slot_depth_mm: number | null;
  slot_width_mm: number | null;
  slot_height_mm: number | null;
}

export interface InventoryBinFormValues {
  flashpcb_bin_barcode: string;
  bin_location: string;
  bin_name: string;
  board_id: string | null;
  bin_type: BinType;
  feeder_details: FeederDetailsFormValues | null;
  tray_details: TrayDetailsFormValues | null;
  can_allocate_from_this_bin: boolean;
}

const InventoryBinForm = ({
  selectedBin,
  onSuccess,
  onCancel,
  hideBoardSelection,
  board,
}: {
  selectedBin: InventoryBin;
  onSuccess: () => void;
  onCancel: () => void;
  hideBoardSelection?: boolean;
  board?: Board;
}) => {
  const { dashboard } = useManufacturingDashboard();
  const customAlert = useCustomAlert();
  const { userId } = useAppSelector((state) => state.auth);
  const newBin = selectedBin.flashpcb_bin_barcode === "";
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(
    board?.id || null,
  );
  const onError = (detail: string) => {
    customAlert(detail);
  };
  const flashPCBSettings = useFlashPCBSettings();
  const { saveBin, loading } = useUpdateInventoryBin(onSuccess, onError);
  useEffect(() => {
    if (selectedBin.board_id !== null && dashboard !== null) {
      setSelectedBoardId(
        dashboard.items.find((item) => {
          return item.board_id == selectedBin.board_id;
        })?.board_id || null,
      );
    }
  }, [dashboard, selectedBin.board_id]);

  const selectBoard = (
    newValue: SingleValue<{
      label: ReactNode;
      value: string;
    }> | null,
  ) => {
    if (newValue !== null) {
      setSelectedBoardId(newValue.value);
    } else {
      setSelectedBoardId(null);
    }
  };

  const itemBoardIds = new Set(dashboard?.items.map((item) => item.board_id));
  const boardOptions = Array.from(itemBoardIds).map((boardId) => ({
    label: (
      <BoardOptionFromId
        boardId={boardId}
        currentUserId={null}
        selected={boardId == selectedBoardId}
      />
    ),
    value: boardId,
  }));

  const getBoardValue = () => {
    if (selectedBoardId === null) {
      return null;
    } else {
      return {
        label: (
          <BoardOptionFromId
            boardId={selectedBoardId}
            currentUserId={userId}
            selected={false}
          />
        ),
        value: selectedBoardId,
      };
    }
  };

  const onSubmit = (values: InventoryBinFormValues) => {
    const binToSave = {
      ...values,
      board_id: selectedBoardId || null,
    };
    if (values.bin_type !== BinType.TRAY) {
      binToSave.tray_details = null;
    } else {
      binToSave.tray_details = {
        num_rows: binToSave.tray_details?.num_rows || null,
        num_columns: binToSave.tray_details?.num_columns || null,
        slot_width_mm: binToSave.tray_details?.slot_width_mm || null,
        slot_depth_mm: binToSave.tray_details?.slot_depth_mm || null,
        slot_height_mm: binToSave.tray_details?.slot_height_mm || null,
      };
    }
    if (values.bin_type !== BinType.FEEDER) {
      binToSave.feeder_details = null;
    }
    saveBin(binToSave, newBin);
  };

  return (
    <>
      <LoadingOverlay loading={loading} text={"Updating bin"} />
      <Formik
        initialValues={{
          ...selectedBin,
          feeder_details: selectedBin.feeder_details || {
            width_mm: null,
          },
          tray_details: selectedBin.tray_details || {
            num_columns: null,
            num_rows: null,
            slot_depth_mm: null,
            slot_width_mm: null,
            slot_height_mm: null,
          },
        }}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={validationSchema}
      >
        {({ handleSubmit, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              {FORM_INFO.map((formLine) => (
                <FormikFormLine
                  autoFocus={formLine.name === "flashpcb_bin_barcode" && newBin}
                  key={formLine.name}
                  name={formLine.name}
                  label={formLine.label}
                  placeholder={formLine.placeholder}
                  isRequired={true}
                  disabled={formLine.name === "flashpcb_bin_barcode" && !newBin}
                />
              ))}
              <FormikSelect
                name={"bin_type"}
                label={"Bin type"}
                options={Object.values(BinType).map((type) => ({
                  label: type,
                  value: type,
                }))}
              />
              {values.bin_type === BinType.FEEDER && (
                <FormikSelect
                  name={"feeder_details.width_mm"}
                  label={"Reel width (feeder bins only)"}
                  options={
                    flashPCBSettings?.reel_size_options.reel_width_mm.map(
                      (option) => ({
                        label: `${option} mm`,
                        value: option,
                      }),
                    ) || []
                  }
                  isClearable
                />
              )}
              {values.bin_type === BinType.TRAY && (
                <>
                  <FormikNumberInput
                    name={"tray_details.num_columns"}
                    label={"Number of columns (tray bins only)"}
                  />
                  <FormikNumberInput
                    name={"tray_details.num_rows"}
                    label={"Number of rows (tray bins only)"}
                  />
                  <FormikNumberInput
                    name={"tray_details.slot_depth_mm"}
                    label={"Slot depth (mm) (tray bins only)"}
                  />
                  <FormikNumberInput
                    name={"tray_details.slot_width_mm"}
                    label={"Slot width (mm) (tray bins only)"}
                  />
                  <FormikNumberInput
                    name={"tray_details.slot_height_mm"}
                    label={"Slot height (mm) (tray bins only)"}
                  />
                </>
              )}
              {!hideBoardSelection && (
                // Using the FormikSelect was too complicated here so we are using generic select with useState
                <FlashPCBSelect
                  label={"Bin is for a specific project (optional)"}
                  name={"board_id"}
                  isLoading={dashboard === null}
                  options={boardOptions}
                  value={getBoardValue()}
                  onChange={selectBoard}
                  isClearable
                />
              )}
              <FormikCheckbox
                name={"can_allocate_from_this_bin"}
                label={"Containers in this bin can be allocated to projects"}
              />
              <ButtonContainer>
                <Button
                  label={"Cancel"}
                  variant={ButtonVariants.SECONDARY}
                  onClick={onCancel}
                />
                <Button
                  label={"Submit"}
                  type={ButtonTypes.SUBMIT}
                  disabled={loading}
                />
              </ButtonContainer>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default InventoryBinForm;
